import {Group, GroupEditingLevelTypes} from '../../../../graphql/groups.datatypes';
import {makeStyles} from '@material-ui/styles';
import moment from 'moment';
import React, {useEffect, useState} from 'react';
import {Link, RouteComponentProps} from 'react-router-dom';
import useRouter from 'use-react-router';
import validate from 'validate.js';
import {
    Button,
    Card,
    CardContent,
    CardHeader,
    colors,
    Divider,
    Grid,
    Link as MuiLink,
    Theme,
    Typography
} from '@material-ui/core';
import availableTours from '../getAvailableTours.api';
import {getHost} from '../../../../graphql/GraphQlProvider';
import {formatMoney} from '../../../../utils/formatters';
import {getBlankGroup} from '../GroupDetails';
import getGroupWithQuery from '../../getGroupWithQuery.api';
import {T} from 'react-polyglot-hooks';
import {gqlGroupBasicQuery, gqlGroupTotalsQuery} from '../../getGroupQueries.api';
import {ConfirmDelete} from '../../../../components/ConfirmDelete';
import SendEmailToGroup from '../../../../components/SendEmailToGroup';
import {PaymentScheduleDetailsModal} from '../../../PaymentSchedule/PaymentScheduleDetailsModal';
import useReactRouter from 'use-react-router';
import deleteGroup from '../deleteGroup.api';
import {useSnackbar} from 'notistack';


const useStyles = makeStyles((theme: Theme) => ({
    root: {
        padding: theme.spacing(3)
    },
    container: {
        marginTop: theme.spacing(3),
        width: '100%',
        marginBottom: theme.spacing(2),
    },
    card: {},
    saveButton: {
        marginLeft: theme.spacing(1),
        color: theme.palette.white,
        backgroundColor: colors.green[600],
        '&:hover': {
            backgroundColor: colors.green[900]
        }
    },
    deleteButton: {
        marginLeft: theme.spacing(1),
        color: theme.palette.white,
        backgroundColor: theme.palette.error.light,
        '&:hover': {
            backgroundColor: theme.palette.error.main
        }
    },
    cloneButton: {
        marginLeft: theme.spacing(1),
        color: theme.palette.white,
        backgroundColor: colors.green[400],
        '&:hover': {
            backgroundColor: colors.green[700]
        }
    },
    errorIcon: {
        marginRight: theme.spacing(1),
        color: theme.palette.error.main,
    },
    marginBottom: {
        paddingLeft: theme.spacing(1),
        marginBottom: theme.spacing(3),
    },
    tabs: {
        marginTop: theme.spacing(3)
    },
    linkButton: {
        marginRight: theme.spacing(1),
        [theme.breakpoints.down('md')]: {
            marginBottom: 10,
        }
    },
    buttonWrapper: {
        [theme.breakpoints.down('md')]: {

            flexWrap: 'wrap',
            marginBottom: 10,
        },
    },
    buttonHeader: {
        [theme.breakpoints.down('md')]: {
            flex: 'auto',
            marginTop: 10,
            marginRight: 0,
            flexWrap: 'wrap',
        }
    }
}));

export const GroupDetailsTotals = (props: { group: Group, isAdmin: boolean, canEdit: boolean }) => {
    const classes = useStyles();
    const {isAdmin, group} = props;
    const isNewGroup = group.id === -1;
    const groupTotalsData = isNewGroup ? {loading: false, data: {group: getBlankGroup()}, error: null} : getGroupWithQuery(Number(group.id), gqlGroupTotalsQuery);
    const groupTotals = groupTotalsData && !groupTotalsData.loading && groupTotalsData.data && groupTotalsData.data.group;
    const {history} = useRouter();
    const showPrices = isAdmin || groupTotals && (moment(groupTotals.end_date).add(15, 'days').diff(moment(), 'days') > 0);
    const [sendDeleteGroup, deleteData] = deleteGroup();
    const [open, setOpen] = React.useState(false);
    const {enqueueSnackbar} = useSnackbar();
    const canDownloadDocsZip = groupTotals && groupTotals.documents && groupTotals.documents.reduce((hasDocs, doc) => hasDocs || !!(doc.completed_documents && doc.completed_documents.length > 0), false)

    //1. Set form and error state
    const [formState, setFormState] = useState({...group});

    //tours
    const toursData = availableTours();

    const currentTZ = moment.tz.guess();
    const currentDate = moment(new Date()).tz(currentTZ).utc()

    const [errors, setErrors] = useState({
        isValid: false,
        errors: {
            name: false,
            description: false,
            start_date: false,
            end_date: false,
            admissions_cutoff: false,
        }
    });

    const selectedTour = (toursData.data && toursData.data.availableTours) ? toursData.data.availableTours.find(tour => Number(tour.id) === formState.tour_resource_id) : null;

    //3. setup validation and error hook
    const validateSchema: any = {
        name: {
            presence: {allowEmpty: false, message: 'is required'},
        },
        description: {
            presence: {allowEmpty: false, message: 'is required'},
        },
        start_date: {
            presence: {allowEmpty: false, message: 'is required'},
        },
        end_date: {
            presence: {allowEmpty: false, message: 'is required'},
        },
        admissions_cutoff: {
            presence: {allowEmpty: false, message: 'is required'},
            datetime: {
                latest: formState && moment.utc(formState.start_date),
                message: formState && '^Admission cutoff must be before the group start date, ' + formState.start_date
            },
        },
        hotel: {
            presence: {allowEmpty: false, message: 'is required'},
        },
        tour: {
            presence: {allowEmpty: false, message: 'is required'},
        },
        description_resort: {
            presence: {allowEmpty: false, message: 'is required'},
        },
        description_whats_included: {
            presence: {allowEmpty: false, message: 'is required'},
        },
        description_checkin: {
            presence: {allowEmpty: false, message: 'is required'},
        },
    };

    if (selectedTour) {
        const calculatedStartDate = moment.utc(selectedTour.available_from).subtract(selectedTour.max_nights_before, 'days');
        const calculatedEndDate = moment.utc(selectedTour.available_to).add(selectedTour.max_nights_after, 'days');

        validateSchema.start_date.datetime = {
            earliest: calculatedStartDate,
            message: `^Start date must be on or after the tour additional night's start date, ${calculatedStartDate.format('YYYY-MM-DD')}`
        };
        validateSchema.end_date.datetime = {
            latest: calculatedEndDate,
            message: `^End date must be on or before the tour additional night's end date, ${calculatedEndDate.format('YYYY-MM-DD')}`
        };
    }

    useEffect(() => {
        const newErrors = validate(formState, validateSchema);

        setErrors(state => ({
            ...state,
            isValid: !newErrors,
            errors: newErrors || {}
        }));
    }, [formState]);

    const [openContactModal, setOpenContactModal] = React.useState({message: '', open: false});
    const handleContactModalOpen = () => setOpenContactModal({message: ``, open: true});
    const handleContactModalClose = () => setOpenContactModal({message: '', open: false});

    const [openPaymentScheduleModal, setOpenPaymentScheduleModal] = React.useState(false);
    const handlePaymentScheduleModalOpen = () => setOpenPaymentScheduleModal(true);
    const handlePaymentScheduleModalClose = () => setOpenPaymentScheduleModal(false);

    const handleClose = () => {
        setOpen(false);
    };

    const handleDelete = () => {
        if (groupTotals) {
            sendDeleteGroup({variables: {id: groupTotals.id}})
                .then(() => {
                    enqueueSnackbar('Group Deleted.', {variant: 'success'});
                    history.push('/groups');
                })
                .catch(() => {
                    enqueueSnackbar('Deleted Group Failed!', {variant: 'error'});
                });

            handleClose();
        }
    };

    return (
        <React.Fragment>
            {!isNewGroup && (
                <React.Fragment>
                    <Card className={classes.container}>
                        {groupTotalsData.loading && (
                            <React.Fragment>
                                <CardHeader title={'Group Totals'}
                                            className={classes.buttonWrapper}
                                            classes={{action: classes.buttonHeader}}/>
                                <Divider/>
                                <CardContent className={classes.card}>
                                    <Grid item xs={4}>
                                        <Typography><T phrase="Loading..."/></Typography>
                                    </Grid>
                                </CardContent>
                            </React.Fragment>
                        )}
                        {groupTotals && !groupTotalsData.loading && (
                            <React.Fragment>
                                <CardHeader title={'Group Totals'}
                                            className={classes.buttonWrapper}
                                            classes={{action: classes.buttonHeader}}
                                            action={isAdmin && [
                                                <Button key={1}
                                                        variant="contained"
                                                        className={classes.linkButton}
                                                        onClick={() => handleContactModalOpen()}>
                                                    Email Group
                                                </Button>,
                                                <Button key={2}
                                                        variant="contained"
                                                        className={classes.linkButton}
                                                        onClick={() => history.push('/admin_booking/' + groupTotals.booking_form_url)}>
                                                    Admin Booking Tool
                                                </Button>,
                                                (!groupTotals.booking_tool_edit_cutoff || moment(groupTotals.booking_tool_edit_cutoff).add(1, 'day').isSameOrAfter(currentDate)) &&
                                                <Button key={3}
                                                        variant="contained"
                                                        className={classes.linkButton}
                                                        component={Link} to={'/booking/' + formState.booking_form_url}>
                                                    Client Booking Tool
                                                </Button>,
                                                groupTotals.payment_schedule &&
                                                <Button key={4}
                                                        variant="contained"
                                                        className={classes.linkButton}
                                                        onClick={() => handlePaymentScheduleModalOpen()}>
                                                    Payment Schedule
                                                </Button>,
                                                canDownloadDocsZip &&
                                                <MuiLink key={5}
                                                         href={getHost() + '/docs/' + btoa(JSON.stringify({'group': groupTotals.id}))}>
                                                    <Button variant="contained">
                                                        Download All Docs
                                                    </Button>
                                                </MuiLink>,
                                            ]}/>
                                <Divider/>
                                {showPrices && (
                                    <CardContent className={classes.card}>
                                        <Grid container
                                              alignItems="center"
                                              spacing={1}>
                                            <Grid item
                                                  sm={3}
                                                  xs={12}>
                                                <Typography variant="h3">
                                                    Grand Total: {formatMoney(groupTotals.cost!, true)}
                                                </Typography>
                                            </Grid>
                                            <Grid item
                                                  sm={3}
                                                  xs={12}>
                                                <Typography variant="h3">
                                                    Processing Fees: {groupTotals.totalCreditCardFees ? formatMoney(groupTotals.totalCreditCardFees!, true) : 'N/A'}
                                                </Typography>
                                            </Grid>
                                            <Grid item
                                                  sm={3}
                                                  xs={12}>
                                                <Typography variant="h3">
                                                    Outstanding: {formatMoney(groupTotals.outstandingBalance!, true)}
                                                </Typography>
                                            </Grid>
                                            <Grid item
                                                  sm={3}
                                                  xs={12}>
                                                <Typography variant="h3">
                                                    Past Due: {formatMoney(groupTotals.overduePayments!, true)}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                )}
                            </React.Fragment>
                        )}
                    </Card>
                    {groupTotals && !groupTotalsData.loading && (
                        <React.Fragment>
                            <ConfirmDelete onClose={handleClose} onDeleteClick={handleDelete} open={open}/>
                            <SendEmailToGroup onClose={handleContactModalClose}
                                              open={openContactModal.open}
                                              groupId={groupTotals.id}
                                              fromEmail={groupTotals.tour && groupTotals.tour.comms_email_address}
                                              heading={'Send Message to Group'}
                                              message={'This message will be sent to every billing party paying member in this group.'}
                            />

                            {groupTotals.payment_schedule && (
                                <PaymentScheduleDetailsModal onClose={handlePaymentScheduleModalClose}
                                                             open={openPaymentScheduleModal}
                                                             paymentScheduleId={groupTotals.payment_schedule.id}/>
                            )}
                        </React.Fragment>
                    )}
                </React.Fragment>
            )}
        </React.Fragment>
    );
};

export default GroupDetailsTotals;
